import { Component, Inject, OnInit, Optional } from '@angular/core';
import { ListButton } from '@app/core/components/list/list.component';
import { ModalService } from '@app/core/services/modal.service';
import { TranslateService } from '@app/core/services/translate.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { RiskConsequence, RiskElementGet, RiskItem, RiskProbability, RiskScore, RiskService } from '@app/services/risk.service';

@Component({ selector: 'tt-risk-element', standalone: false, templateUrl: './risk-element.component.html', styleUrls: ['./risk-element.component.css'] })
export class RiskElementComponent implements OnInit {
    public page?: RiskElementGet;
    public probabilities: RiskProbability[] = [];
    public consequences: RiskConsequence[] = [];
    public risks: RiskScore[] = [];
    public logs: RiskItem[] = [];
    public tab = 'B';
    public saving = false;
    public confirming = false;
    public deleting = false;
    public error = '';
    public params = { riskelement_keyno: '', argtype: '', argvalue: '' };
    private translations = { confirm_title: '', confirm_message: '', confirm_ok_label: '', confirm_cancel_label: '', error: '', ok: '' };
    public readonly logButtons: ListButton<RiskItem>[] = [{ id: 'newtab', icon: 'fas fa-external-link', type: 'secondary', onClick: ({ item }) => { if (item.item_state) this.navigator?.newTab({ to: item.item_state, parms: item.item_parms }); } }];
    constructor(private risk: RiskService, private modal: ModalService, private translate: TranslateService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator) { this.navigator = useNavigatorFor(navigator, this); }
    public async ngOnInit(): Promise<void> {
        const params = this.navigator?.params;
        this.params = { riskelement_keyno: String(params?.['riskelement_keyno'] ?? ''), argtype: String(params?.['argtype'] ?? ''), argvalue: String(params?.['argvalue'] ?? '') };
        await Promise.all([this.loadPage(), this.loadChoices(), this.loadTranslations()]);
    }
    private async loadPage(): Promise<void> {
        try {
            this.page = await this.risk.getElement({ riskelement_keyno: this.params.riskelement_keyno });
            if (typeof this.page.riskelement_keyno === 'string' && this.page.riskelement_keyno.trim() !== '') this.logs = await this.risk.listLogs({ riskelement_keyno: this.page.riskelement_keyno });
        } catch (error) { this.showError(error); }
    }
    private async loadChoices(): Promise<void> {
        const results = await Promise.allSettled([this.risk.listProbabilities(), this.risk.listConsequences(), this.risk.listRisks()]);
        if (results[0].status === 'fulfilled') this.probabilities = results[0].value; else this.showError(results[0].reason);
        if (results[1].status === 'fulfilled') this.consequences = results[1].value; else this.showError(results[1].reason);
        if (results[2].status === 'fulfilled') this.risks = results[2].value; else this.showError(results[2].reason);
    }
    private async loadTranslations(): Promise<void> {
        try { this.translations = { ...this.translations, ...await this.translate.translateBatch(this.translations) }; }
        catch (error) { this.showError(error); }
    }
    public changeRating(field: 'probability_no' | 'consequence_no', value: string): void {
        if (!this.page || value === undefined || this.page[field] === undefined || value === this.page[field]) return;
        this.page[field] = value;
        if (typeof value !== 'string' || value.trim() === '') return;
        this.page.risk_no = String(Number(this.page.probability_no) * Number(this.page.consequence_no));
    }
    public get riskColour(): string {
        const score = this.page?.risk_no;
        if (score && ['1', '2', '3'].includes(score)) return 'green';
        if (score && ['4', '5', '6', '8', '9'].includes(score)) return 'yellow';
        if (score && ['10', '12'].includes(score)) return 'orange';
        if (score && ['15', '16', '20', '25'].includes(score)) return 'red';
        return '';
    }
    public get existing(): boolean { return Number(this.params.riskelement_keyno) > 0; }
    public get hasWork(): boolean { return Number(this.page?.custact_no) > 0; }
    public async save(): Promise<void> {
        if (!this.page || this.saving) return;
        this.saving = true;
        Object.assign(this.page, this.params);
        try { await this.risk.saveElement(this.page); this.saving = false; this.navigator?.back(); }
        catch (error) { this.showError(error); }
        finally { this.saving = false; }
    }
    public async confirm(): Promise<void> {
        if (!this.page || this.confirming) return;
        this.confirming = true;
        try { await this.risk.confirmElement({ riskelement_keyno: this.page.riskelement_keyno }); this.confirming = false; this.navigator?.back(); }
        catch (error) { this.showError(error); }
        finally { this.confirming = false; }
    }
    public async delete(): Promise<void> {
        if (!this.page || this.deleting) return;
        this.deleting = true;
        try {
            if (!await this.modal.openConfirmDialog({ type: 'danger', title: this.translations.confirm_title, message: this.translations.confirm_message, ok: this.translations.confirm_ok_label, cancel: this.translations.confirm_cancel_label })) return;
            await this.risk.deleteElement({ riskelement_keyno: this.page.riskelement_keyno }); this.deleting = false; this.navigator?.back();
        } catch (error) { this.showError(error); }
        finally { this.deleting = false; }
    }
    public openLog(event: { item: RiskItem; event: MouseEvent }): void {
        if (!event.item.item_state || event.item.item_path === '') return;
        const target = { to: event.item.item_state, parms: event.item.item_parms };
        if (event.event.ctrlKey) this.navigator?.newTab(target); else this.navigator?.go(target);
    }
    private showError(error: unknown): void { this.error = error instanceof Error ? error.message : String(error); }
}
